import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, Clock, Hash, Loader2 } from "lucide-react";
import { CareersNav } from "../components/careers/CareersNav";
import { ApplyForm } from "../components/careers/ApplyForm";
import { Wingman } from "../components/careers/Wingman";
import { Footer } from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { API, http, fmtDate } from "../lib/api";

const Meta = ({ icon: Icon, label, value, testid }) => (
  <div className="flex items-center gap-3" data-testid={testid}>
    <Icon className="h-4 w-4 text-emerald-400" />
    <span className="text-sm text-zinc-400">{label}</span>
    <span className="text-sm text-zinc-100">{value}</span>
  </div>
);

export default function JobDetail() {
  const { code } = useParams();
  const [job, setJob] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    window.scrollTo(0, 0);
    http.get(`${API}/careers/jobs/${encodeURIComponent(code)}`)
      .then((r) => { setJob(r.data ?? null); setState(r.data ? "ok" : "missing"); })
      .catch(() => setState("missing"));
  }, [code]);

  return (
    <div className="grain relative overflow-x-clip" data-testid="job-detail-page">
      <CareersNav />
      <main className="mx-auto max-w-[1400px] px-5 pt-32 pb-24 sm:px-8 lg:px-12">
        <Link to="/careers#openings" className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-emerald-300" data-testid="job-back-link">
          <ArrowLeft className="h-4 w-4" /> All openings
        </Link>
        {state === "loading" && <Loader2 className="mt-16 h-6 w-6 animate-spin text-emerald-400" />}
        {state === "missing" && (
          <div className="mt-16" data-testid="job-not-found">
            <p className="font-display text-5xl">Role not found.</p>
            <p className="mt-4 text-zinc-300">This opening may have been filled or removed. <Link to="/careers#talent-pool" className="text-emerald-300 underline">Place your CV on record</Link> instead.</p>
          </div>
        )}
        {state === "ok" && job && (
          <div className="mt-12 grid grid-cols-1 gap-14 lg:grid-cols-12">
            <article className="lg:col-span-7">
              <Reveal>
                <p className="eyebrow">{job.organisation}</p>
                <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl leading-[1.02] font-medium" data-testid="job-title">{job.title}</h1>
                <div className="mt-8 flex flex-col gap-3 border-y border-white/10 py-6 sm:flex-row sm:flex-wrap sm:gap-x-10">
                  <Meta icon={Hash} label="Job ID" value={job.job_code} testid="job-meta-id" />
                  <Meta icon={CalendarDays} label="Posted" value={fmtDate(job.posted_on)} testid="job-meta-posted" />
                  <Meta icon={Clock} label="Apply by" value={fmtDate(job.apply_by)} testid="job-meta-apply-by" />
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="jd-content mt-10" data-testid="job-description" dangerouslySetInnerHTML={{ __html: job.jd_html ?? "" }} />
              </Reveal>
            </article>
            <aside className="lg:col-span-5">
              <div className="lg:sticky lg:top-28 rounded-md border border-white/10 bg-[#111519] p-6 sm:p-8" data-testid="job-apply-card">
                {job.is_active ? (
                  <>
                    <p className="font-display text-3xl">Apply for this role</p>
                    <p className="mt-2 mb-8 text-sm text-zinc-400">Job ID {job.job_code}. Applications close on {fmtDate(job.apply_by)}.</p>
                    <ApplyForm jobCode={job.job_code} testid="job-apply-form" />
                  </>
                ) : (
                  <div data-testid="job-closed">
                    <p className="font-display text-3xl">Applications closed</p>
                    <p className="mt-3 text-sm text-zinc-300">This role stopped accepting applications on {fmtDate(job.apply_by)}.</p>
                    <Link to="/careers#talent-pool" className="mt-6 inline-block text-sm text-emerald-300 underline">Place your CV on record instead</Link>
                  </div>
                )}
              </div>
            </aside>
          </div>
        )}
      </main>
      <Footer />
      <Wingman />
    </div>
  );
}
